import { MoreOutlined, PushpinFilled } from "@ant-design/icons"
import { App, Avatar, Button, Dropdown, Flex, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { type LeadNote } from "../../types/lead.types.ts"
import { useLeadActivityFormat } from "../activity/use-lead-activity-format.ts"
import { OVERVIEW_ACCENTS } from "../overview/OverviewCardIcon.tsx"
import { initialsOf } from "../overview/overview-initials.ts"
import { accentForAuthor } from "./note-author-accent.ts"
import { NOTE_CATEGORY_COLORS } from "./note-category-colors.ts"
import { noteCreatedInstant } from "./note-created-instant.ts"
import { NoteCategoryChip } from "./NoteCategoryChip.tsx"
import { paperTiltForNote } from "./note-paper-tilt.ts"

const { Text, Paragraph } = Typography

/** Rough character count past which the clamp visibly truncates content at
 *  `CONTENT_CLAMP_HEIGHT` — jsdom can't measure real layout, so the "long
 *  note" scenario is driven off content length rather than scrollHeight. */
const LONG_CONTENT_THRESHOLD = 220
const CONTENT_CLAMP_HEIGHT = 96

interface NoteCardProps {
  note: LeadNote
  canEdit?: boolean
  onEdit: (note: LeadNote) => void
  onDelete: (id: string) => void
  onTogglePin: (note: LeadNote) => void
}

function NoteCard({ note, canEdit, onEdit, onDelete, onTogglePin }: NoteCardProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const { formatMoment } = useLeadActivityFormat()
  const [expanded, setExpanded] = useState(false)

  const colors = NOTE_CATEGORY_COLORS[note.category]
  const authorName: string =
    note.author?.name || t("leadWorkspace.cards.notes.unknownAuthor")
  const authorSeed: string = note.author?.id || authorName
  const accent = OVERVIEW_ACCENTS[accentForAuthor(authorSeed)]
  const createdInstant = noteCreatedInstant(note.created)
  const formattedDate = createdInstant ? formatMoment(createdInstant) : ""
  const isLong = note.content.length > LONG_CONTENT_THRESHOLD

  const confirmDelete = () => {
    modal.confirm({
      title: t("leadWorkspace.cards.notes.deleteConfirmTitle"),
      content: t("leadWorkspace.cards.notes.deleteConfirmBody"),
      okText: t("leadWorkspace.cards.notes.deleteOk"),
      okButtonProps: { danger: true },
      cancelText: t("leadWorkspace.card.cancel"),
      onOk: () => onDelete(note.id)
    })
  }

  const tilt = useMemo(() => paperTiltForNote(note.id), [note.id])

  return (
    <div
      data-testid={`note-card-${note.id}`}
      className={"relative flex min-h-[180px] flex-col gap-2 rounded-lg border p-3 pb-4"}
      style={{
        background: colors.background,
        borderColor: colors.border,
        borderBottomRightRadius: 3,
        transform: `rotate(${tilt}deg)`,
        boxShadow: "0 1px 2px rgba(15,23,42,0.05), 0 6px 10px -6px rgba(15,23,42,0.10)"
      }}
    >
      {/* A folded paper corner, drawn with a border triangle — no image, no
          extra dependency. Sits under normal content, never intercepts clicks. */}
      <span
        aria-hidden
        className={"pointer-events-none absolute bottom-0 right-0 h-0 w-0"}
        style={{
          borderStyle: "solid",
          borderWidth: "0 0 16px 16px",
          borderColor: `transparent transparent rgba(15,23,42,0.12) transparent`
        }}
      />

      <Flex justify={"space-between"} align={"flex-start"} gap={8}>
        <Flex gap={8} align={"center"} className={"min-w-0"}>
          <Avatar
            size={28}
            className={"shrink-0"}
            style={{ backgroundColor: accent.background, color: accent.color }}
          >
            {initialsOf(authorName)}
          </Avatar>
          <Flex vertical gap={0} className={"min-w-0"}>
            <Text strong ellipsis className={"text-sm"}>
              {authorName}
            </Text>
            <Text type={"secondary"} className={"text-xs"}>
              {formattedDate}
            </Text>
          </Flex>
        </Flex>

        <Flex gap={4} align={"center"} className={"shrink-0"}>
          {note.is_pinned && (
            <PushpinFilled
              className={"text-sm text-amber-600"}
              title={t("leadWorkspace.cards.notes.pinned")}
              aria-label={t("leadWorkspace.cards.notes.pinned")}
            />
          )}
          {canEdit && (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "edit",
                    label: t("leadWorkspace.cards.notes.edit"),
                    onClick: () => onEdit(note)
                  },
                  {
                    key: "pin",
                    label: t(
                      note.is_pinned
                        ? "leadWorkspace.cards.notes.unpin"
                        : "leadWorkspace.cards.notes.pin"
                    ),
                    onClick: () => onTogglePin(note)
                  },
                  {
                    key: "delete",
                    danger: true,
                    label: t("leadWorkspace.cards.notes.delete"),
                    onClick: confirmDelete
                  }
                ]
              }}
            >
              <Button
                size={"small"}
                type={"text"}
                icon={<MoreOutlined />}
                aria-label={t("leadWorkspace.cards.notes.moreActions")}
              />
            </Dropdown>
          )}
        </Flex>
      </Flex>

      <NoteCategoryChip category={note.category} variant={"label"} />

      <div
        className={expanded ? undefined : "overflow-hidden"}
        style={expanded ? undefined : { maxHeight: CONTENT_CLAMP_HEIGHT }}
      >
        <Paragraph className={"mb-0 whitespace-pre-wrap text-sm leading-relaxed"}>
          {note.content}
        </Paragraph>
      </div>

      {isLong && (
        <button
          type={"button"}
          onClick={() => setExpanded((value) => !value)}
          className={"self-start text-xs font-medium text-blue-600 hover:underline"}
        >
          {expanded
            ? t("leadWorkspace.cards.notes.collapse")
            : t("leadWorkspace.cards.notes.expand")}
        </button>
      )}
    </div>
  )
}

export { NoteCard }
